import React, { useEffect, useMemo, useState } from 'react'
import { Alert, CircularProgress, TextField, Typography } from '@mui/material'
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledButton,
} from '../../styles/StyledComponents'
import { fetchCompanyRoles, type RbacRole } from '../../../../services/rbacService'
import { useRbacStore } from '../../../../stores/rbacStore'
import { describeAccessError } from '../../../../utils/rbacMessages'

const OWNER_ROLE_KEY = 'owner'
const DEFAULT_ROLE_KEY = 'admin'

export type OwnerChangeMode = 'demote' | 'sole'

interface OwnerChangeDialogProps {
  open: boolean
  mode: OwnerChangeMode
  companyId: string
  companyName: string
  /** The Owner being demoted, or the person who becomes the only Owner. */
  target: { userId: string; name: string } | null
  /** The other Owners: they stay Owners on a demotion and get the chosen role on "sole Owner". */
  otherOwnerNames: readonly string[]
  /** Does the change; a throw is shown in the dialog and keeps it open. */
  onConfirm: (roleKey: string) => Promise<void>
  onClose: () => void
}

const listNames = (names: readonly string[]) =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`

/**
 * Platform admins only (SC-439). Asks for the role the demoted people get, then confirms by name:
 * a demotion and a "make sole Owner" both take the people's new role from the admin, never
 * from a default they didn't see.
 */
export const OwnerChangeDialog: React.FC<OwnerChangeDialogProps> = ({
  open,
  mode,
  companyId,
  companyName,
  target,
  otherOwnerNames,
  onConfirm,
  onClose,
}) => {
  const [roles, setRoles] = useState<RbacRole[] | null>(null)
  const [roleKey, setRoleKey] = useState(DEFAULT_ROLE_KEY)
  const [rolesFailed, setRolesFailed] = useState(false)
  const [reloads, setReloads] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const targetId = target?.userId ?? null

  useEffect(() => {
    if (!open || !targetId) return
    let cancelled = false
    setBusy(false)
    setError(null)
    setRoles(null)
    setRoleKey(DEFAULT_ROLE_KEY)
    setRolesFailed(false)
    const loadRoles = async (): Promise<RbacRole[]> => {
      try {
        return await fetchCompanyRoles(companyId)
      } catch {
        // The system roles are the same in every company, so a refused list still allows a choice.
        // The catalogue may not be loaded yet, so wait for it rather than read a snapshot.
        await useRbacStore.getState().ensureCatalogue()
        const system = useRbacStore.getState().catalogue?.systemRoles ?? []
        return system.map<RbacRole>((role) => ({ ...role, isSystem: true, scope: 'global' }))
      }
    }
    void loadRoles().then((list) => {
      if (cancelled) return
      const picks = list.filter((role) => role.key !== OWNER_ROLE_KEY)
      // An empty list would leave Confirm disabled with nothing to explain it.
      if (picks.length === 0) {
        setRolesFailed(true)
        return
      }
      setRoles(picks)
      const hasDefault = picks.some((role) => role.key === DEFAULT_ROLE_KEY)
      setRoleKey(hasDefault ? DEFAULT_ROLE_KEY : picks[0].key)
    })
    return () => {
      cancelled = true
    }
  }, [open, companyId, targetId, reloads])

  const chosen = useMemo(() => roles?.find((role) => role.key === roleKey), [roles, roleKey])

  if (!target) return null

  const submit = async () => {
    if (!roleKey) return
    setBusy(true)
    setError(null)
    try {
      await onConfirm(roleKey)
      onClose()
    } catch (failure) {
      setError(describeAccessError(failure, useRbacStore.getState().catalogue))
    } finally {
      setBusy(false)
    }
  }

  const roleName = chosen?.name ?? roleKey
  const sole = mode === 'sole'
  const others = listNames(otherOwnerNames)
  const one = otherOwnerNames.length === 1
  const summary = sole
    ? `${target.name} becomes the only Owner of ${companyName}.` +
      (others ? ` ${others} ${one ? 'becomes' : 'become'} ${roleName}.` : '')
    : `${target.name} stops being an Owner of ${companyName} and becomes ${roleName}.` +
      (others ? ` ${others} ${one ? 'stays an Owner' : 'stay Owners'}.` : '')

  return (
    <StyledDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>{sole ? 'Make sole Owner' : 'Demote Owner'}</StyledDialogTitle>
      <StyledDialogContent>
        <Typography
          variant="body1"
          sx={{ color: 'text.primary', mb: 2 }}
          data-testid="owner-change-summary"
        >
          {summary}
        </Typography>
        {rolesFailed ? (
          <Alert
            severity="error"
            data-testid="owner-change-roles-error"
            action={
              <StyledButton
                size="small"
                variant="outlined"
                onClick={() => setReloads((n) => n + 1)}
                data-testid="owner-change-roles-retry"
              >
                Try again
              </StyledButton>
            }
          >
            The roles of this workspace could not be loaded.
          </Alert>
        ) : roles === null ? (
          <CircularProgress size={20} data-testid="owner-change-roles-loading" />
        ) : (
          <TextField
            select
            fullWidth
            size="small"
            label={sole ? 'New role for the other Owners' : 'New role'}
            value={roleKey}
            disabled={busy}
            onChange={(event) => setRoleKey(event.target.value)}
            SelectProps={{ native: true }}
            inputProps={{ 'aria-label': 'New role', 'data-testid': 'owner-change-role' }}
            InputLabelProps={{ shrink: true }}
          >
            {roles.map((role) => (
              <option key={role.key} value={role.key}>
                {role.name}
                {role.isSystem ? '' : ' (custom)'}
              </option>
            ))}
          </TextField>
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
          The change is written to the audit log.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="owner-change-error">
            {error}
          </Alert>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledButton onClick={onClose} variant="outlined" disabled={busy}>
          Cancel
        </StyledButton>
        <StyledButton
          onClick={submit}
          variant="contained"
          color="error"
          disabled={busy || roles === null || !roleKey}
          data-testid="owner-change-confirm"
        >
          {busy ? 'Saving...' : sole ? 'Make sole Owner' : 'Demote'}
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  )
}

export default OwnerChangeDialog
